import { useState } from 'react';
import { Wallet } from 'lucide-react';
import { db } from '@/lib/db';
import type { Member, MemberPublic, PostEnriched } from '@/lib/db/types';
import { venmoPay } from '@/lib/venmo';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';

interface Props {
  post: PostEnriched;
  member: Member;
  memberMap: Record<string, MemberPublic>;
  isNew?: boolean;
  onClose: () => void;
  onSettled: () => void;
}

export function WagerModal({ post, member, memberMap, isNew, onClose, onSettled }: Props) {
  const [amount, setAmount] = useState(isNew ? 20 : (post.wager ? post.wager.amount_cents / 100 : 0));
  const [note, setNote] = useState(isNew ? '' : (post.wager?.note || ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const author = post.author;

  const createAndPay = async () => {
    if (amount <= 0) {
      setError('Enter a wager amount');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await db.wagers.create({
        post_id: post.id,
        to_member_id: author.id,
        amount_cents: Math.round(amount * 100),
        note: note.trim() || undefined,
      });
      venmoPay(author.venmo);
      onSettled();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Unable to create wager');
    } finally {
      setSubmitting(false);
    }
  };

  if (isNew) {
    return (
      <Sheet title={`Wager with ${author.name.split(' ')[0]}`} onClose={onClose}>
        <div className="space-y-4 p-4 pb-8">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          <div>
            <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">AMOUNT</div>
            <div className="relative">
              <span className="absolute left-4 top-3 font-serif text-2xl font-bold text-gold">$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="w-full rounded-xl border border-line bg-paper py-3 pl-10 text-center font-serif text-3xl font-bold"
              />
            </div>
            <div className="mt-2 flex gap-2">
              {[5, 10, 20, 50, 100].map((v) => (
                <button key={v} type="button" onClick={() => setAmount(v)} className={`flex-1 rounded py-2 text-xs font-semibold ${amount === v ? 'bg-gold text-navy border border-gold' : 'border border-line bg-paper text-ink-soft'}`}>
                  ${v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">NOTE</div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Nassau — back nine press"
              className="w-full rounded-xl border border-line bg-paper px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void createAndPay()}
            className="flex w-full items-center justify-center gap-2 rounded py-3.5 text-[13px] font-bold tracking-wide text-white disabled:opacity-50"
            style={{ background: '#3D95CE' }}
          >
            <Wallet size={16} /> {submitting ? 'Creating…' : `SEND $${amount} VIA VENMO`}
          </button>
          <p className="text-center text-[11px] text-mute">Opens Venmo with {author.venmo || 'recipient'} pre-filled</p>
        </div>
      </Sheet>
    );
  }

  const wager = post.wager;
  if (!wager) return null;
  const fromM = memberMap[wager.from_member_id];
  const toM = memberMap[wager.to_member_id];
  const iAmPayer = wager.from_member_id === member.id;
  const settled = wager.status === 'paid';

  const markPaid = async () => {
    await db.wagers.markPaid(wager.id);
    onSettled();
    onClose();
  };

  return (
    <Sheet title="Wager Details" onClose={onClose}>
      <div className="space-y-4 p-4 pb-8">
        <div className="rounded-xl border border-gold/40 bg-navy p-6 text-center text-cream">
          <div className="text-[10px] font-bold tracking-widest text-gold">{settled ? 'SETTLED' : 'PENDING'}</div>
          <div className="font-serif text-5xl font-bold text-gold">${(wager.amount_cents / 100).toFixed(0)}</div>
          {wager.note && <div className="mt-2 text-sm italic text-white/70">&ldquo;{wager.note}&rdquo;</div>}
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-line bg-cream p-3">
          {fromM && <Avatar member={fromM} size={32} />}
          <div className="flex-1 text-sm">
            <span className="font-bold">{fromM?.name.split(' ')[0]}</span>
            <span className="text-ink-soft"> owes </span>
            <span className="font-bold">{toM?.name.split(' ')[0]}</span>
          </div>
          {toM && <Avatar member={toM} size={32} />}
        </div>
        {!settled && (
          <>
            {iAmPayer && (
              <button
                type="button"
                onClick={() => { venmoPay(toM?.venmo); setTimeout(() => void markPaid(), 800); }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#3D95CE] py-3.5 text-sm font-bold text-white"
              >
                <Wallet size={16} /> PAY ${(wager.amount_cents / 100).toFixed(0)} VIA VENMO
              </button>
            )}
            <button type="button" onClick={() => void markPaid()} className="w-full rounded-xl border border-green-600 py-3 text-sm font-bold text-green-700">
              MARK AS PAID (CASH/CLUB CHARGE)
            </button>
          </>
        )}
        {settled && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center font-semibold text-green-700">
            Wager settled
          </div>
        )}
      </div>
    </Sheet>
  );
}
